/**
 * Sports Page
 * Main sports betting page with Sports Market UI
 * Implements BACK/LAY betting with proper color system
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useSportsEnabled } from '@/hooks/useSports';
import { useBetting } from '@/Context/BettingContext';
import SportsTabs from './components/SportsTabs/SportsTabs';
import SportsMarket from './components/SportsMarket/SportsMarket';
import AllSportsList from './components/AllSportsList/AllSportsList';
import SportsBetSlip from './components/SportsBetSlip/SportsBetSlip';
import PopularMatches from './components/PopularMatches/PopularMatches';
import ComingSoon from './components/ComingSoon/ComingSoon';
import MyBets from './components/MyBets/MyBets';
import styles from './Sports.module.css';

// Sport ID mapping (from old code - complete list)
const sportPathToId = {
  'popular': null,
  'all-sports': null,
  'soccer': '1',
  'tennis': '2',
  'basketball': '7522',
  'cricket': '4',
  'american-football': '6423',
  'baseball': '7511',
  'boxing': '6',
  'golf': '3',
  'horse-racing': '7',
  'rugby-league': '1477',
  'rugby-union': '5',
  'darts': '3503',
  'snooker': '6422',
  'ice-hockey': '7512',
  'mma': '26420387',
  'greyhound-racing': '4339',
  'motorsport': '8',
  'gaelic-games': '2152880',
};

const Sports = () => {
  const { isMobile } = useScreenSize();
  const { sport, tab } = useParams(); // tab can be 'in-play', 'today', 'tomorrow'
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedBets, setSelectedBets } = useBetting();
  const [showBetSlip, setShowBetSlip] = useState(false);
  const prevBetsLengthRef = useRef(0);

  // Open betslip on mobile when a bet is added via odds click
  useEffect(() => {
    // Only open if we're on mobile, bet count increased, and we had 0 bets before
    if (isMobile && selectedBets.length > prevBetsLengthRef.current && prevBetsLengthRef.current === 0) {
      setShowBetSlip(true);
    }
    prevBetsLengthRef.current = selectedBets.length;
  }, [selectedBets.length, isMobile]);

  // Clear betslip data when closing the modal
  const handleCloseBetslip = () => {
    setShowBetSlip(false);
    // Clear all selected bets when closing
    setSelectedBets([]);
    prevBetsLengthRef.current = 0; // Reset ref when clearing
  };

  // Check if we're on a match detail route - if so, don't render anything (handled by App.jsx routing)
  const isMatchDetailRoute = location.pathname.includes('/sports/markets/');
  if (isMatchDetailRoute) {
    return null; // Match detail is handled by separate route in App.jsx
  }

  // Default to 'popular' when landing on /sports
  useEffect(() => {
    if (location.pathname === '/sports' || location.pathname === '/sports/') {
      navigate('/sports/popular', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Check if we're on /sports/all-sports route (either /sports/all-sports or /sports/:tab/all-sports)
  const isAllSportsRoute = location.pathname === '/sports/all-sports' || sport === 'all-sports';

  // Check if we're on /sports/popular route
  const isPopularRoute = location.pathname === '/sports/popular';

  // Check if we're on /sports/my-bets route
  const isMyBetsRoute = location.pathname === '/sports/my-bets' || tab === 'my-bets';

  // Determine current tab (in-play, today, tomorrow, my-bets) and sport
  const currentTab = tab || 'in-play';
  // If on popular route, set to popular, otherwise check all-sports or sport param
  // If no sport param and not on all-sports/popular route, show all sports in market grid
  const currentSport = isPopularRoute ? 'popular' : (isAllSportsRoute ? 'all-sports' : (sport || null));
  const sportId = (currentSport === 'all-sports' || currentSport === 'popular' || !currentSport) ? null : sportPathToId[currentSport] || null;

  // Don't auto-open betslip - only open when user explicitly clicks

  // Use React Query hook for sports status check
  const { data: sportsStatus, isLoading } = useSportsEnabled();
  const sportsEnabled = sportsStatus?.sportsEnabled ?? false;

  // Only show loader on initial load (when no data exists yet)
  if (isLoading) {
    return (
      <div className={styles.loaderContainer}>
        <div className={styles.loader}>
          <span className={styles.loaderText}>loading</span>
          <span className={styles.load}></span>
        </div>
      </div>
    );
  }

  if (!sportsEnabled) {
    return <ComingSoon />;
  }

  // Show All Sports List only when explicitly on /sports/all-sports route
  // Otherwise show market grid with all sports
  const showAllSportsList = isAllSportsRoute;
  
  // Show Popular Matches for Popular tab
  const showPopular = currentSport === 'popular';
  
  // Show My Bets content when on my-bets route
  const showMyBets = isMyBetsRoute;

  return (
    <div className={styles.sportsPage}>
      {/* Sports Tabs - Hide scrollable sports tabs when on /sports/all-sports or /sports/my-bets */}
      <SportsTabs 
        currentSport={currentSport} 
        sportId={sportId} 
        currentTab={currentTab}
        hideSportsTabs={isAllSportsRoute || isMyBetsRoute}
      />



      {/* Show My Bets content */}
      {showMyBets ? (
        <MyBets />
      ) : showPopular ? (
        <>
          {isMobile ? (
            <>
              <PopularMatches />
      
              <SportsBetSlip isOpen={showBetSlip} onClose={handleCloseBetslip} />
            </>
          ) : (
            <div className={styles.desktopLayout}>
              <div className={styles.marketSection}>
                <PopularMatches />
               
              </div>
              <div className={styles.betSlipSection}>
                <SportsBetSlip />
              </div>
            </div>
          )}
        </>
      ) : showAllSportsList ? (
        <AllSportsList currentTab={isAllSportsRoute ? 'in-play' : currentTab} />
      ) : (
        <>
          {/* Unified Sports Market and Bet Slip */}
          {isMobile ? (
            <>
              <SportsMarket sportId={sportId} upperTab={currentTab} sport={currentSport} />
              <SportsBetSlip isOpen={showBetSlip} onClose={handleCloseBetslip} />
            </>
          ) : (
            <div className={styles.desktopLayout}>
              <div className={styles.marketSection}>
                <SportsMarket sportId={sportId} upperTab={currentTab} sport={currentSport} />
              </div>
              <div className={styles.betSlipSection}>
                <SportsBetSlip />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Sports;
